import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Save, X, Plus, Settings as SettingsIcon, ShoppingCart, Users, Bell, BellOff, Send, ShieldCheck, Building2, MapPin, Database, RefreshCw, Droplets, FileSpreadsheet, CalendarDays } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import usePushNotifications from '../hooks/usePushNotifications';
import SearchableSelect from '../components/SearchableSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsPage = ({ user }) => {
  const [settings, setSettings] = useState(null);
  const push = usePushNotifications();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newRecipient, setNewRecipient] = useState('');
  const [newSiteAddress, setNewSiteAddress] = useState('');
  const [migrating, setMigrating] = useState(false);
  const [migrateResult, setMigrateResult] = useState(null);

  const [formData, setFormData] = useState({
    completed_tasks_days_threshold: 30,
    price_alert_threshold_percentage: 20.0,
    alert_recipients: [],
    auto_approve_po_threshold: 5000,
    purchase_incharge_email: '',
    store_po_threshold: 10000,
    allow_admin_role_management: false,
    site_addresses: [],
    diesel_ob_factor: 0.507,
    diesel_coal_shale_factor: 0.705,
    sccl_factors: {},
    review_dept_heads: {
      maintenance_head: '',
      stores_head: '',
      production_head: '',
      purchase_head: '',
      additional_reviewers: [],
    },
  });

  const [materialTypes, setMaterialTypes] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const updateScclFactor = (matName, val) => {
    setFormData(f => ({
      ...f,
      sccl_factors: { ...f.sccl_factors, [matName]: parseFloat(val) || 0 }
    }));
  };

  const handleImportSkusFromAtlas = async () => {
    setMigrating(true);
    setMigrateResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/migrate/import-skus-from-atlas`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000,
      });
      setMigrateResult(res.data);
      toast.success(`Migration complete — ${res.data.skus_copied} SKUs copied to production.`);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setMigrateResult({ status: 'error', message: msg });
      toast.error(`Migration failed: ${msg}`);
    } finally {
      setMigrating(false);
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [settingsRes, usersRes, matRes] = await Promise.all([
        axios.get(`${API}/settings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/production/material-types`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      
      setSettings(settingsRes.data);
      setMaterialTypes(matRes.data || []);
      setFormData({
        completed_tasks_days_threshold: settingsRes.data.completed_tasks_days_threshold || 30,
        price_alert_threshold_percentage: settingsRes.data.price_alert_threshold_percentage || 20.0,
        alert_recipients: settingsRes.data.alert_recipients || [],
        auto_approve_po_threshold: settingsRes.data.auto_approve_po_threshold || 5000,
        purchase_incharge_email: settingsRes.data.purchase_incharge_email || 'none',
        store_po_threshold: settingsRes.data.store_po_threshold || 10000,
        allow_admin_role_management: settingsRes.data.allow_admin_role_management || false,
        site_addresses: settingsRes.data.site_addresses || [],
        diesel_ob_factor: settingsRes.data.diesel_ob_factor ?? 0.507,
        diesel_coal_shale_factor: settingsRes.data.diesel_coal_shale_factor ?? 0.705,
        sccl_factors: settingsRes.data.sccl_factors || {},
        review_dept_heads: settingsRes.data.review_dept_heads || {
          maintenance_head: '',
          stores_head: '',
          production_head: '',
          purchase_head: '',
          additional_reviewers: [],
        },
      });
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      // Convert "none" back to empty string for purchase_incharge_email
      const dataToSave = {
        ...formData,
        purchase_incharge_email: formData.purchase_incharge_email === 'none' ? '' : formData.purchase_incharge_email
      };
      await axios.put(`${API}/settings`, dataToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addRecipient = () => {
    if (!newRecipient) return;
    
    if (formData.alert_recipients.includes(newRecipient)) {
      toast.error('User already added');
      return;
    }
    
    setFormData({
      ...formData,
      alert_recipients: [...formData.alert_recipients, newRecipient]
    });
    setNewRecipient('');
  };

  const removeRecipient = (email) => {
    setFormData({
      ...formData,
      alert_recipients: formData.alert_recipients.filter(r => r !== email)
    });
  };

  const getUserName = (email) => {
    const foundUser = users.find(u => u.email === email);
    return foundUser ? foundUser.full_name : email;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-600 mt-1">Configure application settings</p>
      </div>

      <div className="bg-white rounded-lg border shadow-sm divide-y">
        {/* Task Settings */}
        <div className="p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <SettingsIcon className="h-5 w-5" />
            Task Settings
          </h2>
          <div className="space-y-4">
            <div>
              <Label>Completed Tasks Threshold (days)</Label>
              <p className="text-sm text-slate-500 mb-2">
                Tasks completed more than this many days ago will be flagged as "old completed"
              </p>
              <Input
                type="number"
                min="1"
                value={formData.completed_tasks_days_threshold}
                onChange={(e) => setFormData({
                  ...formData,
                  completed_tasks_days_threshold: parseInt(e.target.value) || 30
                })}
                className="max-w-xs"
              />
            </div>
          </div>
        </div>

        {/* Purchase Order Settings */}
        <div className="p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Purchase Order Settings
          </h2>
          <div className="space-y-4">
            <div>
              <Label>Auto-Approve PO Threshold (₹)</Label>
              <p className="text-sm text-slate-500 mb-2">
                Purchase requisitions below this value will be auto-approved
              </p>
              <Input
                type="number"
                min="0"
                value={formData.auto_approve_po_threshold}
                onChange={(e) => setFormData({
                  ...formData,
                  auto_approve_po_threshold: parseFloat(e.target.value) || 5000
                })}
                className="max-w-xs"
              />
            </div>
            <div>
              <Label>Store Team PO Threshold (₹)</Label>
              <p className="text-sm text-slate-500 mb-2">
                Store team can only view/manage purchase orders below this value
              </p>
              <Input
                type="number"
                min="0"
                value={formData.store_po_threshold}
                onChange={(e) => setFormData({
                  ...formData,
                  store_po_threshold: parseFloat(e.target.value) || 10000
                })}
                className="max-w-xs"
              />
            </div>
            <div>
              <Label>Purchase Incharge</Label>
              <p className="text-sm text-slate-500 mb-2">
                This user will receive all purchase-related notifications and price alerts
              </p>
              <SearchableSelect
                value={formData.purchase_incharge_email}
                onValueChange={(val) => setFormData({ ...formData, purchase_incharge_email: val })}
                options={[
                  { value: 'none', label: 'None' },
                  ...users.map(u => ({ value: u.email, label: `${u.full_name} (${u.email})` }))
                ]}
                placeholder="Select purchase incharge"
                className="max-w-xs"
              />
            </div>
          </div>
        </div>

        {/* Price Alert Settings */}
        <div className="p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">
            Price Alert Settings
          </h2>
          <div className="space-y-4">
            <div>
              <Label>Price Alert Threshold (%)</Label>
              <p className="text-sm text-slate-500 mb-2">
                Alert when a purchase price exceeds the average/market price by this percentage
              </p>
              <Input
                type="number"
                min="0"
                step="0.1"
                value={formData.price_alert_threshold_percentage}
                onChange={(e) => setFormData({
                  ...formData,
                  price_alert_threshold_percentage: parseFloat(e.target.value) || 20.0
                })}
                className="max-w-xs"
              />
            </div>
          </div>
        </div>

        {/* Alert Recipients */}
        <div className="p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Users className="h-5 w-5" />
            Alert Recipients
          </h2>
          <p className="text-sm text-slate-500 mb-4">
            Select additional users who will receive inventory and price alerts
          </p>
          
          <div className="flex gap-2 mb-4">
            <SearchableSelect
              value={newRecipient}
              onValueChange={setNewRecipient}
              options={users
                .filter(u => !formData.alert_recipients.includes(u.email))
                .map(u => ({ value: u.email, label: `${u.full_name} (${u.email})` }))}
              placeholder="Select a user"
              className="flex-1"
            />
            <Button
              onClick={addRecipient}
              disabled={!newRecipient}
              variant="outline"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          {formData.alert_recipients.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No additional recipients added</p>
          ) : (
            <div className="space-y-2">
              {formData.alert_recipients.map((email) => (
                <div
                  key={email}
                  className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded border"
                >
                  <div>
                    <p className="font-medium text-slate-900">{getUserName(email)}</p>
                    <p className="text-sm text-slate-500">{email}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeRecipient(email)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Access Control — Super Admin Only */}
      {user?.is_super_admin && (
        <div className="bg-white rounded-lg border shadow-sm">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-indigo-600" />
              Access Control
            </h2>
            <div className="flex items-center justify-between py-3 border rounded-lg px-4">
              <div>
                <p className="font-medium text-slate-900 text-sm">Allow admins to manage roles</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  When enabled, regular admins can create, edit, and delete custom roles in addition to super admins.
                </p>
              </div>
              <Switch
                checked={formData.allow_admin_role_management}
                onCheckedChange={v => setFormData({ ...formData, allow_admin_role_management: v })}
                data-testid="allow-admin-role-management-switch"
              />
            </div>
          </div>
        </div>
      )}

      {/* Database Migration — Super Admin Only */}
      {user?.is_super_admin && (
        <div className="bg-white rounded-lg border shadow-sm">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-1 flex items-center gap-2">
              <Database className="h-5 w-5 text-teal-600" />
              Database Migration
            </h2>
            <p className="text-sm text-slate-500 mb-4">
              Import the 2004 SKUs from the Atlas database into the production database.
              Safe to run multiple times — existing SKUs are skipped.
            </p>
            <div className="flex items-center gap-4">
              <Button
                data-testid="migrate-skus-btn"
                onClick={handleImportSkusFromAtlas}
                disabled={migrating}
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                {migrating ? (
                  <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Importing SKUs...</>
                ) : (
                  <><Database className="h-4 w-4 mr-2" />Import 2004 SKUs from Atlas</>
                )}
              </Button>
              {migrateResult && (
                <span className={`text-sm font-medium ${migrateResult.status === 'error' ? 'text-red-600' : 'text-green-700'}`}>
                  {migrateResult.status === 'complete'
                    ? `✓ ${migrateResult.skus_copied} copied, ${migrateResult.skus_skipped} already existed`
                    : migrateResult.message}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Diesel Efficiency Factors */}
      <div className="bg-white rounded-lg border p-6 space-y-4">
        <div className="flex items-center gap-2 text-lg font-semibold text-slate-800">
          <Droplets className="h-5 w-5 text-blue-600" />
          Diesel Efficiency Factors
        </div>
        <p className="text-sm text-slate-500">These factors (liters per m³) are used on the Daily Report tab to calculate diesel eligibility vs consumed.</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>OB (Overburden) Factor (L/m³)</Label>
            <Input
              type="number"
              step="0.001"
              value={formData.diesel_ob_factor}
              onChange={e => setFormData(f => ({ ...f, diesel_ob_factor: parseFloat(e.target.value) || 0 }))}
              className="mt-1.5"
              data-testid="diesel-ob-factor-input"
            />
          </div>
          <div>
            <Label>Coal &amp; Shale Factor (L/m³)</Label>
            <Input
              type="number"
              step="0.001"
              value={formData.diesel_coal_shale_factor}
              onChange={e => setFormData(f => ({ ...f, diesel_coal_shale_factor: parseFloat(e.target.value) || 0 }))}
              className="mt-1.5"
              data-testid="diesel-coal-shale-factor-input"
            />
          </div>
        </div>
      </div>

      {/* SCCL Report Settings */}
      <div className="bg-white rounded-lg border p-6 space-y-4">
        <div className="flex items-center gap-2 text-lg font-semibold text-slate-800">
          <FileSpreadsheet className="h-5 w-5 text-green-600" />
          SCCL Report — Cum/Trip Factors
        </div>
        <p className="text-sm text-slate-500">
          Set the standardised cum/trip values used when generating the SCCL client report. These override the machine-measured values in the Excel export.
        </p>
        {materialTypes.length === 0 ? (
          <p className="text-sm text-slate-400 italic">No material types configured yet. Add them from the Production page → Configure Materials.</p>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            {materialTypes.map(mt => (
              <div key={mt.name}>
                <Label>{mt.name} — cum/trip</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.sccl_factors[mt.name] ?? mt.cum_per_trip ?? ''}
                  onChange={e => updateScclFactor(mt.name, e.target.value)}
                  className="mt-1.5"
                  placeholder={`e.g. ${mt.cum_per_trip || 17}`}
                  data-testid={`sccl-factor-${mt.name}`}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-slate-900 hover:bg-slate-800"
        >
          {saving ? (
            <>Saving...</>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>

      {/* ── Push Notifications ──────────────────────────────── */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm" data-testid="push-notifications-section">
        <div className="flex items-center gap-2 mb-4">
          <Bell className="h-5 w-5 text-indigo-600" />
          <h2 className="text-base font-semibold text-slate-800">Push Notifications</h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Enable desktop &amp; mobile push notifications. You'll receive alerts for breakdowns, critical tasks, and maintenance reminders — even when the app is closed.
        </p>

        {!push.supported ? (
          <p className="text-sm text-amber-600 bg-amber-50 rounded-lg p-3">
            Push notifications are not supported in this browser. Please use Chrome, Firefox, Edge, or Safari 16.4+.
          </p>
        ) : push.permission === 'denied' ? (
          <p className="text-sm text-red-600 bg-red-50 rounded-lg p-3">
            Push notifications are blocked. Please enable them in your browser settings for this site, then reload.
          </p>
        ) : (
          <div className="flex items-center gap-4">
            {push.isSubscribed ? (
              <>
                <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 rounded-lg px-3 py-2 border border-green-200">
                  <Bell className="w-4 h-4" />
                  <span className="font-medium">Push notifications enabled on this device</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    const ok = await push.unsubscribe();
                    if (ok) toast.success('Push notifications disabled');
                    else toast.error('Failed to disable push');
                  }}
                  disabled={push.isLoading}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="disable-push-btn"
                >
                  <BellOff className="w-4 h-4 mr-1" /> Disable
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    const token = localStorage.getItem('token');
                    try {
                      await fetch(`${API}/push/test`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
                      toast.success('Test notification sent!');
                    } catch { toast.error('Failed to send test'); }
                  }}
                  data-testid="test-push-btn"
                >
                  <Send className="w-4 h-4 mr-1" /> Send Test
                </Button>
              </>
            ) : (
              <Button
                onClick={async () => {
                  const ok = await push.subscribe();
                  if (ok) toast.success('Push notifications enabled!');
                  else toast.error('Failed to enable push notifications');
                }}
                disabled={push.isLoading}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="enable-push-btn"
              >
                <Bell className="w-4 h-4 mr-2" />
                {push.isLoading ? 'Setting up...' : 'Enable Push Notifications'}
              </Button>
            )}
          </div>
        )}
        <p className="text-xs text-slate-400 mt-3">
          Each device needs to be enabled separately. Alerts go to users configured in "Alert Recipients" above.
        </p>
      </div>

      {/* Site Addresses */}
      <div className="bg-white rounded-xl border shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="h-5 w-5 text-slate-600" />
          <h2 className="text-lg font-semibold text-slate-900">Site / Consignee Addresses</h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          These addresses appear as selectable "Delivery Site" options when creating or editing purchase orders.
        </p>
        <div className="space-y-2 mb-3">
          {(formData.site_addresses || []).map((addr, i) => (
            <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-md px-3 py-2">
              <Building2 className="h-4 w-4 text-slate-400 shrink-0" />
              <span className="flex-1 text-sm">{addr}</span>
              <button
                onClick={() => setFormData(f => ({ ...f, site_addresses: f.site_addresses.filter((_, idx) => idx !== i) }))}
                className="text-red-400 hover:text-red-600"
                data-testid={`remove-address-${i}`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="Enter site address..."
            value={newSiteAddress}
            onChange={e => setNewSiteAddress(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && newSiteAddress.trim()) {
                setFormData(f => ({ ...f, site_addresses: [...(f.site_addresses || []), newSiteAddress.trim()] }));
                setNewSiteAddress('');
              }
            }}
            className="h-8"
            data-testid="new-site-address-input"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              if (newSiteAddress.trim()) {
                setFormData(f => ({ ...f, site_addresses: [...(f.site_addresses || []), newSiteAddress.trim()] }));
                setNewSiteAddress('');
              }
            }}
            data-testid="add-site-address-btn"
          >
            <Plus className="h-4 w-4 mr-1" />Add
          </Button>
        </div>
        <p className="text-xs text-slate-400 mt-2">Press Enter or click Add to save an address.</p>
      </div>

      {/* Monthly Review — Department Heads */}
      <div className="p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-1 flex items-center gap-2">
          <CalendarDays className="h-5 w-5" />
          Monthly Review — Department Heads
        </h2>
        <p className="text-sm text-slate-500 mb-4">
          These users will be notified and have access to review open indents and partial deliveries at end of month.
        </p>
        <div className="space-y-4">
          {[
            { key: 'maintenance_head', label: 'Maintenance Head' },
            { key: 'stores_head',      label: 'Stores Head' },
            { key: 'production_head',  label: 'Production Head' },
            { key: 'purchase_head',    label: 'Purchase Head' },
          ].map(({ key, label }) => (
            <div key={key}>
              <Label>{label}</Label>
              <SearchableSelect
                value={formData.review_dept_heads?.[key] || 'none'}
                onValueChange={(val) =>
                  setFormData(f => ({
                    ...f,
                    review_dept_heads: {
                      ...(f.review_dept_heads || {}),
                      [key]: val === 'none' ? '' : val,
                    },
                  }))
                }
                options={[
                  { value: 'none', label: 'None' },
                  ...users.map(u => ({ value: u.email, label: `${u.full_name} (${u.email})` })),
                ]}
                placeholder={`Select ${label}`}
                className="max-w-xs"
                data-testid={`review-${key}-select`}
              />
            </div>
          ))}
          <div>
            <Label>Additional Reviewers</Label>
            <p className="text-xs text-slate-500 mb-2">These users can also view and act on the monthly review.</p>
            <div className="flex gap-2 mb-2">
              <SearchableSelect
                value={newRecipient}
                onValueChange={setNewRecipient}
                options={users
                  .filter(u => !(formData.review_dept_heads?.additional_reviewers || []).includes(u.email))
                  .map(u => ({ value: u.email, label: `${u.full_name} (${u.email})` }))}
                placeholder="Add reviewer"
                className="flex-1"
              />
              <Button
                variant="outline"
                disabled={!newRecipient}
                onClick={() => {
                  if (!newRecipient) return;
                  setFormData(f => ({
                    ...f,
                    review_dept_heads: {
                      ...(f.review_dept_heads || {}),
                      additional_reviewers: [...(f.review_dept_heads?.additional_reviewers || []), newRecipient],
                    },
                  }));
                  setNewRecipient('');
                }}
                data-testid="add-additional-reviewer-btn"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {(formData.review_dept_heads?.additional_reviewers || []).length === 0 ? (
              <p className="text-sm text-slate-400 italic">No additional reviewers added</p>
            ) : (
              <div className="space-y-1">
                {(formData.review_dept_heads?.additional_reviewers || []).map((email) => (
                  <div key={email} className="flex items-center justify-between bg-slate-50 rounded-md px-3 py-2">
                    <span className="text-sm">{getUserName(email)} ({email})</span>
                    <button
                      onClick={() =>
                        setFormData(f => ({
                          ...f,
                          review_dept_heads: {
                            ...(f.review_dept_heads || {}),
                            additional_reviewers: (f.review_dept_heads?.additional_reviewers || []).filter(e => e !== email),
                          },
                        }))
                      }
                      className="text-red-400 hover:text-red-600 ml-2"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
